import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { 
  Building2, 
  UserPlus, 
  Mail, 
  Check, 
  X, 
  Loader2,
  Clock,
  Trash2
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface CompanyMember {
  id: string;
  member_email: string;
  member_name: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  invited_at: string;
  accepted_at: string | null;
}

interface CompanyMemberManagerProps {
  userId: string;
  plan: string;
  isVisible: boolean;
}

const MAX_COMPANY_MEMBERS = 10; // Firma plan allows up to 10 members

export function CompanyMemberManager({ userId, plan, isVisible }: CompanyMemberManagerProps) {
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const { toast } = useToast();

  const canAddMore = members.length < MAX_COMPANY_MEMBERS;
  const isFirmaPlan = plan === 'firma';

  // Check if user is a company owner (not just a member with firma plan)
  const checkOwnerStatus = async () => {
    if (!userId) return;
    
    try {
      // Check if this user is listed as owner_id in klartext_company_members
      const { data, error } = await supabase
        .from('klartext_company_members')
        .select('id')
        .eq('owner_id', userId)
        .limit(1);

      if (error) throw error;
      
      // If user has entries as owner_id, they are an owner
      // Also check if they are NOT a member (member_user_id) themselves
      const { data: memberData } = await supabase
        .from('klartext_company_members')
        .select('id')
        .eq('member_user_id', userId)
        .limit(1);
      
      // User is owner if they have entries as owner OR if they have no member entry
      // (meaning they are original firma purchaser, not invited)
      const hasOwnerEntries = data && data.length > 0;
      const isInvitedMember = memberData && memberData.length > 0;
      
      setIsOwner(hasOwnerEntries || !isInvitedMember);
    } catch (error) {
      console.error('Error checking owner status:', error);
      setIsOwner(false);
    }
  };

  const fetchMembers = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('klartext_company_members')
        .select('*')
        .eq('owner_id', userId)
        .order('invited_at', { ascending: false });

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching company members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && userId && isFirmaPlan) {
      checkOwnerStatus();
      fetchMembers();
    }
  }, [isVisible, userId, isFirmaPlan]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !canAddMore) return;

    setInviting(true);
    try {
      // Call backend function to handle invitation + optional email sending
      const { data, error } = await supabase.functions.invoke('klartext-company-invite', {
        body: {
          member_email: inviteEmail.trim().toLowerCase(),
          member_name: inviteName.trim() || null,
        }
      });

      if (error) {
        throw new Error(error.message || 'Einladung konnte nicht gesendet werden.');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.member) {
        setMembers(prev => [data.member, ...prev]);
      }

      const emailSent = Boolean(data?.email_sent);

      setInviteEmail('');
      setInviteName('');
      toast({
        title: 'Einladung gesendet',
        description: emailSent
          ? `Eine Einladungs-E-Mail wurde an ${inviteEmail} gesendet.`
          : `Einladung erstellt für ${inviteEmail}.`,
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Einladung konnte nicht gesendet werden.',
        variant: 'destructive',
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('klartext_company_members')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setMembers(prev => prev.filter(m => m.id !== id));
      toast({
        title: 'Mitarbeiter entfernt',
        description: 'Der Mitarbeiter wurde entfernt.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: 'Mitarbeiter konnte nicht entfernt werden.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Only show to owners, not to invited members
  if (!isVisible || !isFirmaPlan || !isOwner) return null;

  const statusConfig = {
    pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Ausstehend' },
    accepted: { icon: Check, color: 'text-green-600', bg: 'bg-green-50', label: 'Aktiv' },
    rejected: { icon: X, color: 'text-red-600', bg: 'bg-red-50', label: 'Abgelehnt' }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5" />
          Mitarbeiter verwalten
        </CardTitle>
        <CardDescription>
          Laden Sie bis zu {MAX_COMPANY_MEMBERS} Mitarbeiter ein, um unbegrenzte Checks zu nutzen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Invite Form */}
        <form onSubmit={handleInvite} className="space-y-3 mb-4">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Name (optional)"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              disabled={!canAddMore || inviting}
              className="w-1/3"
            />
            <Input
              type="email"
              placeholder="E-Mail-Adresse"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={!canAddMore || inviting}
              className="flex-1"
            />
            <Button 
              type="submit" 
              disabled={!canAddMore || inviting || !inviteEmail.trim()}
              className="gap-2"
            >
              {inviting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Einladen
            </Button>
          </div>
        </form>

        <p className="text-sm text-slate-500 mb-4">
          {members.length} / {MAX_COMPANY_MEMBERS} Mitarbeiter
        </p>

        {/* Members List */}
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <Building2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Noch keine Mitarbeiter eingeladen.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => {
              const status = statusConfig[member.status];
              const StatusIcon = status.icon;

              return (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${status.bg}`}
                >
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-slate-500" />
                    <div>
                      {member.member_name && (
                        <p className="font-medium text-slate-900">{member.member_name}</p>
                      )}
                      <p className={member.member_name ? "text-sm text-slate-600" : "font-medium text-slate-900"}>
                        {member.member_email}
                      </p>
                      <div className="flex items-center gap-1 text-xs">
                        <StatusIcon className={`h-3 w-3 ${status.color}`} />
                        <span className={status.color}>{status.label}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(member.id)}
                    disabled={deletingId === member.id}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                  >
                    {deletingId === member.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
